import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ChevronDown, ChevronRight, Pencil, Trash2 } from "lucide-react";
import { observer } from "mobx-react-lite";
import { useMemo, useCallback, useState } from "react";
import {
  useAssignmentStore,
  useDisclosureStore,
  useEntryDataStore,
  useEntryUIStore,
} from "../../hooks/useDataEntryStore";
import { canEditDisclosure } from "../../domains/assignment";
import {
  filterOrgEntries,
  formatTotal,
  sortEntries,
} from "../../domains/dataEntry";
import {
  bulkSoftDeleteDataEntries,
  syncNxmapEntry,
} from "../../operations/entry.ops";
import EntryEmptyState from "./EntryEmptyState";
import EntryFilterBar from "./EntryFilterBar";
import EntryTable from "./EntryTable";
import OrgGroup from "./OrgGroup";
import TargetLabel from "./TargetLabel";
import { useAuthStore } from "@/features/auth";
import { Can } from "@/hooks/useCan/Can";
import { ConfirmDialog } from "@/components/common";
import { useTran } from "@/hooks";
import ReportingPeriodLabel from "../common/ReportingPeriodLabel";

const SubEntryGroup = ({
  index,
  title,
  total,
  entries,
  inputType,
  onProof,
  onSync,
  permissions,
}) => {
  const [expanded, setExpanded] = useState(true);
  const [sort, setSort] = useState({ key: "reportingMonth", direction: "asc" });
  const Chevron = expanded ? ChevronDown : ChevronRight;

  const handleSort = (key) => {
    setSort((prev) =>
      prev.key === key
        ? { key, direction: prev.direction === "asc" ? "desc" : "asc" }
        : { key, direction: "asc" },
    );
  };

  const sorted = useMemo(() => sortEntries(entries, sort), [entries, sort]);

  return (
    <div className="ml-3">
      <div className="flex items-center justify-between py-2">
        <button
          type="button"
          className="flex items-center gap-2 text-sm"
          onClick={() => setExpanded((v) => !v)}
        >
          <Chevron size={14} />
          <Badge variant="outline">SE{index + 1}</Badge>
          <span className="font-medium">{title}</span>
        </button>
        {total ? (
          <span className="text-sm font-semibold text-primary">{total}</span>
        ) : (
          <span className="text-sm font-semibold text-primary">N/A</span>
        )}
      </div>
      {expanded && (
        <EntryTable
          entries={sorted}
          inputType={inputType}
          editable={false}
          onProof={onProof}
          onSync={onSync}
          permissions={permissions}
          sort={sort}
          onSort={handleSort}
        />
      )}
    </div>
  );
};

const YearGroup = ({
  year,
  total,
  subEntryGroups,
  editable,
  disclosureId,
  orgId,
  onEdit,
  onDelete,
  onEditAll,
  onDeleteAll,
  onProof,
  onSync,
  permissions,
  reportingYears,
}) => {
  const [expanded, setExpanded] = useState(true);
  const Chevron = expanded ? ChevronDown : ChevronRight;

  return (
    <div className="border border-border rounded-lg overflow-hidden">
      <div className="flex items-center justify-between px-4 py-2.5">
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="flex items-center gap-1.5 font-semibold text-sm"
            onClick={() => setExpanded((v) => !v)}
          >
            <Chevron size={14} />
            <ReportingPeriodLabel
              reportingYears={reportingYears}
              year={year}
              direction="horizontal"
            />
          </button>
          <TargetLabel
            disclosureId={disclosureId}
            organizationId={orgId}
            year={year}
          />
        </div>
        <div className="flex items-center gap-2">
          {total ? (
            <span className="text-sm font-semibold text-primary">{total}</span>
          ) : (
            <span className="text-sm font-semibold text-primary">N/A</span>
          )}
          {editable && (
            <Can
              resource="esg_data_entry"
              action="update"
              userPermissions={permissions}
            >
              <Button
                variant="outline"
                size="sm"
                onClick={() => onEditAll?.(year)}
              >
                <Pencil size={14} />
                Edit All
              </Button>
            </Can>
          )}
          {editable && (
            <Can
              resource="esg_data_entry"
              action="delete"
              userPermissions={permissions}
            >
              <Button
                variant="outline"
                size="sm"
                className="text-destructive hover:text-destructive"
                onClick={() => onDeleteAll?.(year)}
              >
                <Trash2 size={14} />
                Delete All
              </Button>
            </Can>
          )}
        </div>
      </div>
      {expanded && (
        <div className="px-4 pb-2 space-y-2">
          {subEntryGroups.map((seg) => (
            <SubEntryGroup
              key={seg.index}
              {...seg}
              editable={editable}
              onEdit={onEdit}
              onDelete={onDelete}
              onProof={onProof}
              onSync={onSync}
              permissions={permissions}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const SubEntryView = observer(({ reportingYears = [] }) => {
  const { t } = useTran();
  const { activeDisclosure } = useDisclosureStore();
  const { getAssignment } = useAssignmentStore();
  const { subEntries, orgList, orgLookupMap } = useEntryDataStore();
  const {
    selectedOrg,
    selectedYear,
    searchEntry,
    setEditingEntry,
    setEditingSubEntries,
    setDeletingEntryId,
    setProofEntry,
    bulkDeleteContext,
    setBulkDeleteContext,
    deletingEntry: isDeleting,
  } = useEntryUIStore();
  const { user } = useAuthStore();
  const permissions = user?.permissions || [];

  const assignment = getAssignment(activeDisclosure?._id);
  const editable = canEditDisclosure(assignment);

  const handleEdit = (entry) => setEditingEntry(entry);
  const handleDelete = (entryId) => setDeletingEntryId(entryId);
  const handleProof = (entry) => setProofEntry(entry);
  const handleSync = (entryId) => syncNxmapEntry(entryId);

  const hasCreatePermission = permissions.includes("esg_data_entry:create");

  const handleEditAll = (orgId, year) => {
    const yearData = subEntries?.dataEntries?.[orgId]?.[year];
    if (!yearData) return;
    setEditingSubEntries({
      orgId,
      year,
      groupedEntries: yearData.entries || {},
    });
  };

  const handleDeleteAll = (orgId, year) => {
    const yearData = subEntries?.dataEntries?.[orgId]?.[year];
    if (!yearData) return;
    const entryIds = Object.values(yearData.entries || {}).flatMap((group) =>
      (group.entries || []).map((e) => e._id),
    );
    if (!entryIds.length) return;
    setBulkDeleteContext({ entryIds, year, count: entryIds.length });
  };

  const handleConfirmBulkDelete = async () => {
    if (!bulkDeleteContext?.entryIds?.length) return;
    const ok = await bulkSoftDeleteDataEntries(bulkDeleteContext.entryIds);
    if (ok) setBulkDeleteContext(null);
  };

  const subEntryDefs = activeDisclosure?.subEntries || [];

  const orgMap = useMemo(
    () => new Map(orgList.map((org) => [org.id, org])),
    [orgList],
  );

  const mapYearData = useCallback(
    (year, yearData, search) => {
      const {
        entries: groupedEntries = {},
        formulaResult,
        totalValue,
      } = yearData;

      const total = formulaResult
        ? formatTotal(formulaResult.value, formulaResult.unit)
        : formatTotal(totalValue, null);

      const subEntryGroups = Object.entries(groupedEntries)
        .map(([index, group]) => {
          const def = subEntryDefs[Number(index)];
          const filtered = search
            ? group.entries.filter((e) =>
                e.label?.toLowerCase().includes(search),
              )
            : group.entries;

          return {
            index: Number(index),
            title: def?.title || `Sub Entry ${Number(index) + 1}`,
            total: formatTotal(group.totalValue, group.baseUnit),
            entries: filtered,
            inputType: def?.inputType || "value",
          };
        })
        .filter((seg) => seg.entries.length > 0);

      if (!subEntryGroups.length) return null;

      return { year: Number(year), total, subEntryGroups };
    },
    [subEntryDefs],
  );

  const filteredOrgs = useMemo(
    () =>
      filterOrgEntries(
        subEntries?.dataEntries,
        orgMap,
        { selectedOrg, selectedYear, searchEntry },
        mapYearData,
        orgLookupMap,
      ),
    [
      subEntries,
      orgMap,
      selectedOrg,
      selectedYear,
      searchEntry,
      mapYearData,
      orgLookupMap,
      orgLookupMap.size,
    ],
  );

  const hasEntries = Object.keys(subEntries?.dataEntries || {}).length > 0;

  if (!hasEntries)
    return <EntryEmptyState hasCreatePermission={hasCreatePermission} />;

  return (
    <div className="space-y-4">
      <EntryFilterBar />

      <div className="divide-y-[1px] divide-border">
        {filteredOrgs.map((org) => (
          <OrgGroup key={org.orgId} {...org}>
            {org.years.map((yg) => (
              <YearGroup
                key={yg.year}
                {...yg}
                disclosureId={activeDisclosure?._id}
                orgId={org.orgId}
                editable={editable}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onEditAll={(year) => handleEditAll(org.orgId, year)}
                onDeleteAll={(year) => handleDeleteAll(org.orgId, year)}
                onProof={handleProof}
                onSync={handleSync}
                permissions={permissions}
                reportingYears={reportingYears}
              />
            ))}
          </OrgGroup>
        ))}
      </div>

      <ConfirmDialog
        open={!!bulkDeleteContext}
        onOpenChange={(v) => !v && setBulkDeleteContext(null)}
        title={t("Delete All Entries")}
        description={
          bulkDeleteContext
            ? t(
                `Delete ${bulkDeleteContext.count} entries for ${bulkDeleteContext.year}? This action cannot be undone.`,
              )
            : ""
        }
        confirmLabel={t("Delete All")}
        cancelLabel={t("Cancel")}
        onConfirm={handleConfirmBulkDelete}
        loading={isDeleting}
        tone="danger"
      />
    </div>
  );
});

export default SubEntryView;
